import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { DeviceAccessTokens } from "@app/shared/src/api/api.gen";
import { Device } from "@app/shared/src/models";
import { Btn, Card, CardHead, formatAgo, Note, Spinner, Tag } from "@app/shared/src/components_v2";
import { cabCardUrl } from "../../helpers/cabLinks";
import { downloadCabCardPdf } from "../../helpers/cabCardPdf";
import { useLoad } from "../../hooks/useLoad";

/**
 * The cab card: the QR a driver scans to reach this truck without a login.
 *
 * The QR encodes a revocable access token — never the device key, which is a permanent identity
 * secret. Issuing a new card revokes the old one, so a reprint invalidates every copy still taped in
 * a cab or sitting in someone's bookmarks; that rotation story is the whole reason this exists as
 * its own credential.
 */
export const CabCardSection: React.FC<{ device: Device }> = ({ device }) => {
    const tokens = useLoad(() => DeviceAccessTokens.getForDevice(device.id), [device.id]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string>();
    const [copied, setCopied] = useState(false);

    const active = tokens.data?.find((t) => !t.revokedAt && (!t.expiresAt || new Date(t.expiresAt) > new Date()));
    // Absolute on purpose — this is what gets printed. Points at the wzcab app (see cabLinks.ts).
    const cabUrl = active ? cabCardUrl(active.token) : undefined;

    const run = async (action: () => Promise<unknown>) => {
        setBusy(true);
        setError(undefined);
        try {
            await action();
            await tokens.reload();
        } catch (err) {
            setError(err instanceof Error ? err.message : "That didn't work");
        } finally {
            setBusy(false);
        }
    };

    const copy = async () => {
        if (!cabUrl) return;
        try {
            await navigator.clipboard.writeText(cabUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        } catch {
            setError("Couldn't copy — select the link and copy it manually");
        }
    };

    return (
        <Card>
            <CardHead title="Cab card" note="QR access for the driver">
                {active && <Tag tone="safe">Active</Tag>}
            </CardHead>

            {tokens.isLoading ? (
                <div className="grid place-items-center p-8 text-[var(--muted)]">
                    <Spinner />
                </div>
            ) : active && cabUrl ? (
                <div className="flex flex-col gap-4 p-5 sm:flex-row">
                    {/* White backing regardless of theme: this block is what gets printed and taped
                        in a cab, and a QR needs contrast, not theming. */}
                    <div className="flex flex-none flex-col items-center gap-2 self-start rounded-[14px] bg-white p-4">
                        <div className="text-[14px] font-bold text-[#0e141c]">{device.name}</div>
                        <QRCodeSVG value={cabUrl} size={148} marginSize={1} />
                        <div className="font-mono text-[10.5px] tracking-wide text-[#5c6875]">{device.deviceKey}</div>
                    </div>

                    <div className="min-w-0 flex-1">
                        <p className="text-[12.5px] leading-relaxed text-[var(--muted)]">
                            Scanning opens this truck's in-cab screen — no login. Drivers can bookmark it; the link
                            keeps working until the card is replaced or revoked.
                        </p>
                        <p className="mt-2 break-all font-mono text-[11px] text-[var(--faint)]">{cabUrl}</p>
                        <p className="mt-1 text-[11px] text-[var(--faint)]">
                            Issued {formatAgo(active.dateCreated)}
                            {active.lastUsedAt ? ` · last used ${formatAgo(active.lastUsedAt)}` : " · not used yet"}
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <Btn
                                onClick={async () => {
                                    try {
                                        await downloadCabCardPdf({
                                            title: device.facilityName || device.name || "Unit",
                                            deviceKey: device.deviceKey,
                                            url: cabUrl,
                                        });
                                    } catch (err) {
                                        setError(err instanceof Error ? err.message : "Could not build the cab card");
                                    }
                                }}
                            >
                                Download cab card (PDF)
                            </Btn>
                            <Btn tone="quiet" onClick={copy}>
                                {copied ? "Copied" : "Copy link"}
                            </Btn>
                            <Btn tone="quiet" disabled={busy} onClick={() => run(() => DeviceAccessTokens.issue(device.id))}>
                                Reissue
                            </Btn>
                            <Btn tone="danger" disabled={busy} onClick={() => run(() => DeviceAccessTokens.revoke(active.id))}>
                                Revoke
                            </Btn>
                        </div>
                        <p className="mt-2 text-[11px] leading-relaxed text-[var(--faint)]">
                            Reissuing or revoking kills every printed copy and bookmark of the current card immediately.
                        </p>
                    </div>
                </div>
            ) : (
                <div className="p-5">
                    <p className="text-[13px] leading-relaxed text-[var(--muted)]">
                        This truck has no active cab card. Without one, drivers can't reach the in-cab screen.
                    </p>
                    <Btn className="mt-3" disabled={busy} onClick={() => run(() => DeviceAccessTokens.issue(device.id))}>
                        Issue a cab card
                    </Btn>
                </div>
            )}

            {error && <p className="px-5 pb-4 text-[12.5px] text-[var(--crit)]">{error}</p>}

            {(tokens.data?.filter((t) => t.revokedAt).length ?? 0) > 0 && (
                <Note>
                    {tokens.data!.filter((t) => t.revokedAt).length} previous card
                    {tokens.data!.filter((t) => t.revokedAt).length === 1 ? "" : "s"} revoked.
                </Note>
            )}
        </Card>
    );
};
